import { useEffect, useState } from 'react';
import { FUND_TYPES, STATES, CARES_TYPE_VALUES } from '../../../../constants/constants';
import { formatNumber } from '../../../../utils/formatters';
import { benefitService } from '../../../../services/benefitService';

/**
 * Centralized benefit grant calculation logic.
 * Used by CARES only.
 *
 * Responsibilities:
 * - Decide WHEN a calculation should run
 * - Build the correct request payload
 * - Call benefitService.calculateGrant
 * - Write calculated values back into form state
 */
export const useBenefitGrantCalculation = ({
  form,
  formData,
  setFormField,
}) => {
  const [isGrantCalculationPending, setIsGrantCalculationPending] = useState(false);
  const fundType = form?.fundType;
  const state = formData?.state;
  const arrearsLiabilityAmount = form?.arrearsLiabilityAmount;
  const caresType = form?.caresType;

  /**
   * Small helper to run a debounced grant calculation.
   * Keeps each useEffect focused on business rules,
   * not timing / async boilerplate.
   */
  const runGrantCalculation = (buildRequest, handleResponse) => {
    const request = buildRequest();
    if (!request) {
      setIsGrantCalculationPending(false);
      return () => {};
    }

    setIsGrantCalculationPending(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await benefitService.calculateGrant(request);
        if (!response?.success) {
          setIsGrantCalculationPending(false);
          return;
        }

        handleResponse(response);
      } catch (err) {
        console.error('Grant calculation failed', err);
      } finally {
        setIsGrantCalculationPending(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
      setIsGrantCalculationPending(false);
    };
  };

  /* =========================================================
    CARES (WA only – required arrears liability)
    ========================================================= */
  useEffect(() => {
    return runGrantCalculation(
      () => {
        if (fundType !== FUND_TYPES.CARES) return null;
        if (state !== STATES.WA) return null;
        if (CARES_TYPE_VALUES.NEWGRANT !== caresType) return null;
        if (!arrearsLiabilityAmount) return null;

        return {
          arrearsLiabilityAmount: parseFloat(arrearsLiabilityAmount),
        };
      },
      (response) => {
        // Guarded sets ensure idempotent effects and prevent re-fetch loops
        const normalizedEnergyDiscount = response.energyDiscount ?? '';
        const normalizedArrearage = response.arrearage ?? '';
        const normalizedIncomeMeasurementType = response.incomeMeasurementType ?? '';
        const normalizedIncomeMeasurementPercentage = formatNumber(response.incomeMeasurementPercentage, 2) ?? '';
        const normalizedPledgeAmount = formatNumber(response.calculatedPledgeAmount) ?? '';
        if (form.energyDiscount !== normalizedEnergyDiscount)
          setFormField('energyDiscount', normalizedEnergyDiscount);
        if (form.arrearage !== normalizedArrearage)
          setFormField('arrearage', normalizedArrearage);
        if (form.incomeMeasurementType !== normalizedIncomeMeasurementType)
          setFormField('incomeMeasurementType', normalizedIncomeMeasurementType);
        if (form.incomeMeasurementPercentage !== normalizedIncomeMeasurementPercentage)
          setFormField('incomeMeasurementPercentage', normalizedIncomeMeasurementPercentage);
        if (form.pledgeAmount !== normalizedPledgeAmount)
          setFormField('pledgeAmount', normalizedPledgeAmount);
      }
    );
  }, [
    fundType,
    state,
    arrearsLiabilityAmount,
    caresType,
  ]);

  return { isGrantCalculationPending };
};